import React, { useState } from 'react';
import { notification, Space, Tag } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import {
  ICustomer,
  ISearchCustomerReq,
  ISearchCustomerRes,
} from '@/interfaces/customer';
import { EditFilled, DeleteFilled, RedoOutlined } from '@ant-design/icons';
import { deleteCustomer, recoverCustomer } from '@/adapters/customer';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import ConfirmRecover from '@/components/modal/confirm-recover';
import { useNavigate } from 'react-router-dom';

interface IProps {
  loading: boolean;
  criteria: ISearchCustomerReq;
  customer: ISearchCustomerRes;
  onDelete: (id: number) => void;
  onEditData: (item: ICustomer) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ICustomer> | SorterResult<ICustomer>[],
  ) => void;
}

const _customerModal: IDataModal<ICustomer> = {
  open: false,
};

const CustomerTable = ({
  loading,
  criteria,
  customer,
  onDelete,
  onEditData,
  onTableChange,
}: IProps) => {
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] =
    useState<IDataModal<ICustomer>>(_customerModal);
  const [recoverModal, setRecoverModal] =
    useState<IDataModal<ICustomer>>(_customerModal);

  const onDeleteCustomer = async () => {
    try {
      await deleteCustomer(Number(deleteModal?.id));
      notification.success({
        message: 'ลบลูกค้าสำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._customerModal });
    } catch (error) {
      console.log(error);
    }
  };

  const onRecoverCustomer = async () => {
    try {
      await recoverCustomer(Number(recoverModal?.id));
      notification.success({
        message: 'ลบลูกค้าสำเร็จ',
      });
      onDelete(Number(recoverModal?.id || 0));
      setRecoverModal({ ..._customerModal });
    } catch (error) {
      console.log(error);
    }
  };

  const columns: TableProps<ICustomer>['columns'] = [
    {
      title: 'รหัส',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 130,
      fixed: 'left',
      align: 'center',
      render: (_, rec) => (
        <Space
          onClick={() => navigate(`/customer/${rec.id}`)}
          className="text-blue-700 underline cursor-pointer"
        >
          {_}
        </Space>
      ),
    },
    {
      sorter: true,
      title: 'ชื่อลูกค้า',
      dataIndex: 'name',
      key: 'name',
      className: 'font-bold',
      render: _ => <div className="truncate">{_}</div>,
    },
    {
      sorter: true,
      title: 'เลขผู้เสียภาษี',
      dataIndex: 'taxId',
      key: 'taxId',
      width: 150,
    },
    {
      title: 'เบอร์ติดต่อ',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      className: 'font-medium',
      width: 160,
    },
    {
      title: 'เครดิตเทอม',
      dataIndex: 'creditTerm',
      key: 'creditTerm',
      className: 'font-medium',
      align: 'center',
      width: 120,
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      className: 'font-medium',
      align: 'center',
      width: 80,
    },
    {
      title: 'อำเภอ',
      dataIndex: 'districtNameTh',
      key: 'districtNameTh',
      width: 180,
    },
    {
      sorter: true,
      title: 'จังหวัด',
      dataIndex: 'provinceNameTh',
      key: 'provinceNameTh',
      width: 130,
    },
    {
      title: 'ผู้ขาย',
      dataIndex: 'salesName',
      key: 'salesName',
      width: 130,
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (_: boolean) => {
        return (
          <Tag color={_ ? `green` : 'red'} className="min-w-[60px] text-center">
            {_ ? 'ใช้งานอยู่' : 'ปิดงานใข้งาน'}
          </Tag>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          {rec.isDelete ? (
            <RedoOutlined
              onClick={() =>
                setRecoverModal({
                  open: true,
                  item: rec,
                  id: rec.id,
                })
              }
            />
          ) : (
            <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
              <DeleteFilled
                className="text-red-700 px-1.5"
                onClick={() =>
                  setDeleteModal({
                    open: true,
                    item: rec,
                    id: rec.id,
                  })
                }
              />
              <EditFilled
                className="text-blue-500 px-1.5"
                onClick={() => onEditData(rec)}
              />
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<ICustomer>
        columns={columns}
        criteria={criteria}
        data={customer}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1600}
      />
      <DeleteConfirmModal
        content={`คุณต้องการลบลูกค้า '${deleteModal?.item?.name}' ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._customerModal });
        }}
        handleOk={() => {
          onDeleteCustomer();
        }}
        open={deleteModal?.open}
        title={`ลูกค้า '${deleteModal?.item?.name}'`}
      />
      <ConfirmRecover
        content={`คุณต้องการ Recover '${recoverModal?.item?.name}' ใช่หรือไม่`}
        handleCancel={() => {
          setRecoverModal({ ..._customerModal });
        }}
        handleOk={() => {
          onRecoverCustomer();
        }}
        open={recoverModal?.open}
        title={`ลูกค้า '${recoverModal?.item?.name}'`}
      />
    </div>
  );
};

export default CustomerTable;
